import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { ApolloProvider } from '@apollo/client'

import { useAuth } from '../OIDCProvider'
import getStudentClientOIDC from '../../../lib/clients/getStudentClientOIDC'

type StudentApolloProviderProps = {
  /** Children React Components, most likely the App component */
  children: React.ReactNode
}

const StudentApolloProvider = ({ children }: StudentApolloProviderProps) => {
  const { isAuthenticated, getTokenSilently } = useAuth()

  // Keep a ref to the latest getTokenSilently so the Apollo client is never
  // torn down when the token refreshes. The ref always points to the current
  // function, but the stable wrapper passed to Apollo never changes identity.
  const getTokenRef = useRef(getTokenSilently)
  useEffect(() => {
    getTokenRef.current = getTokenSilently
  }, [getTokenSilently])

  const stableGetToken = useCallback(
    (options?: { forceRefresh?: boolean }) => getTokenRef.current(options),
    []
  )

  const client = useMemo(
    () => getStudentClientOIDC(stableGetToken, isAuthenticated),
    [isAuthenticated, stableGetToken]
  )

  return <ApolloProvider client={client}>{children}</ApolloProvider>
}

export default StudentApolloProvider
